import React, { useEffect, useState, useMemo, useRef, useLayoutEffect, useCallback } from "react";
import { Icon, Tooltip, Tag, Popconfirm, message, Popover, Col, Row } from "antd";
import { fetchOsdDetails } from "../../../api/OrdersApi";
import TableComponent from "../Table_v2";
import Gallery from "../../../components/common/Gallery";
import { NoteDeleteIcon } from "../../../common/CustomIcon";
import I18n from "../../../common/I18n";


const FILTER_STATUS_MAP = {
  All: null,
  Open: "OPEN",
  Reviewed: "REVIEW",
  Claim: "CLAIM",
  Closed: "CLOSED",
};

const ReasonTagsCell = ({ reasons }) => {
  const cellRef = useRef(null);
  const measureRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(reasons.length);

  const measure = useCallback(() => {
    const cell = cellRef.current;
    const m = measureRef.current;
    if (!cell || !m) return;
    const containerWidth = cell.clientWidth || 220;
    const tags = Array.from(m.querySelectorAll(".osd-reason-tag"));
    const plusTag = m.querySelector(".osd-reason-plus");
    const plusWidth = plusTag ? plusTag.offsetWidth + 4 : 48;
    let used = 0;
    let count = 0;
    for (let i = 0; i < tags.length; i++) {
      used += tags[i].offsetWidth + 4;
      if (used + plusWidth <= containerWidth) count = i + 1;
      else break;
    }
    setVisibleCount(Math.max(count, 1));
  }, []);

  useLayoutEffect(() => {
    if (!reasons || !reasons.length) return;
    measure();
  }, [measure, reasons]);

  useEffect(() => {
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  if (!reasons || !reasons.length) return null;

  const overflow = reasons.length - visibleCount;

  return (
    <div className="osd-reason-cell" ref={cellRef} style={{ position: "relative" }}>
      {reasons.slice(0, visibleCount).map(r => (
        <Tag key={r} className="osd-reason-tag" color="magenta">{r}</Tag>
      ))}
      {overflow > 0 && (
        <Popover
          placement="top"
          content={
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxWidth: 280, maxHeight: 200, overflowY: "auto" }}>
              {reasons.slice(visibleCount).map(r => (
                <Tag key={r} color="magenta">{r}</Tag>
              ))}
            </div>
          }
          trigger="hover"
        >
          <Tag className="osd-reason-plus" color="magenta">+{overflow}</Tag>
        </Popover>
      )}
      <div
        ref={measureRef}
        style={{ position: "absolute", left: 0, top: 0, zIndex: -1, visibility: "hidden", pointerEvents: "none", whiteSpace: "nowrap" }}
      >
        {reasons.map(r => (
          <Tag key={r} className="osd-reason-tag" color="magenta">{r}</Tag>
        ))}
        <Tag className="osd-reason-plus" color="magenta">+{reasons.length}</Tag>
      </div>
    </div>
  );
};

const getWarehouseName = (warehouses, id) => {
  if (!id && id !== 0) return "";
  const wh = (warehouses || []).find(w => String(w.id) === String(id) || String(w._id) === String(id));
  return wh?.location_code || wh?.code || wh?.warehouse_code || wh?.name || String(id);
};

const buildOsdColumns = ({ warehouses = [], getImages, openGallery, onEdit, onDelete }) => [
  {
    title: 'Reason',
    dataIndex: 'osd_reason',
    key: 'osd_reason',
    width: 220,
    render: (text) => {
      if (!text) return null;
      const reasons = text.split(', ').filter(Boolean);
      return <ReasonTagsCell reasons={reasons} />;
    },
  },
  {
    title: 'Item / Label',
    dataIndex: 'item_name',
    key: 'item_name',
    width: 160,
    render: (text, record) => (
      <div>
        <div>{record.item_name || '-'}</div>
        {record.lpn && <div className="osd-muted">{record.lpn}</div>}
      </div>
    ),
  },
  {
    title: 'Quantity',
    dataIndex: 'qty',
    key: 'qty',
    width: 100,
    render: (text, record) => (
      <span className="osd-qty">{text || 0} {record.packaging_type || record.units || ''}</span>
    ),
  },
  {
    title: I18n.t("general.warehouse"),
    dataIndex: 'warehouse_id',
    key: 'warehouse_id',
    width: 100,
    sorter: (a, b) =>
      getWarehouseName(warehouses, a.warehouse_id).localeCompare(
        getWarehouseName(warehouses, b.warehouse_id)
      ),
    render: (text) => {
      if (!text) return <span className="osd-muted">-</span>;
      return <div>{getWarehouseName(warehouses, text)}</div>;
    },
  },
  {
    title: 'Leg',
    dataIndex: 'leg',
    key: 'leg',
    width: 100,
    render: (text) => <div>{text || <span className="osd-muted">-</span>}</div>,
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    width: 110,
    render: (text) => {
      const colors = { OPEN: '#2563eb', REVIEW: '#f59e0b', CLAIM: '#ef4444', CLOSED: '#10b981' };
      const color = colors[text] || '#9ca3af';
      return (
        <span className="osd-status-badge" style={{ borderColor: color, color }}>
          {text || '-'}
        </span>
      );
    },
  },
  {
    title: 'Reported By / Date',
    dataIndex: 'authorized_by',
    key: 'reported_by_date',
    width: 180,
    render: (text, record) => (
      <div>
        <div className="osd-reported-name">{text || '-'}</div>
        <div className="osd-muted">{record.reported_date || '-'}</div>
      </div>
    ),
  },
  {
    title: "Images",
    dataIndex: "picture_url",
    key: "picture_url",
    width: 120,
    render: (text, record) => {
      const images = getImages(record);
      if (!images.length) return <div className="osd-muted">-</div>;
      const firstImage = images[0];
      const extraCount = images.length - 1;

      return (
        <Row type="flex" align="middle" gutter={10}>
          <Col>
            <img
              className="osd-image-thumb"
              src={firstImage.picture_url}
              alt={firstImage.file_name || "OSD image"}
              onClick={() => openGallery(record)}
            />
          </Col>
          <Col>
            {extraCount > 0 && (
              <span style={{ color: "#4559B0", fontSize: "15px" }}>+{extraCount}</span>
            )}
          </Col>
        </Row>
      );
    },
  },
  {
    title: 'Notes',
    dataIndex: 'notes',
    key: 'notes',
    width: 60,
    render: (text) => text
      ?
      <Popover
        placement="top"
        content={text}
        trigger="hover"
      >
        <Icon type="file-text" style={{ fontSize: 16, color: '#6b7280', cursor: 'pointer' }} />
      </Popover>
      : null,
  },
  {
    title: 'Actions',
    key: 'actions',
    width: 80,
    render: (_, record, index) => (
      <div className="osd-actions-cell">
        <Icon type="edit" theme="twoTone" onClick={() => onEdit?.(record, index)} />
        <Popconfirm
          title="Delete this OSD detail?"
          okText="Delete"
          okButtonProps={{ danger: true }}
          cancelText="Cancel"
          onConfirm={() => onDelete?.(record)}
        >
          <NoteDeleteIcon />
        </Popconfirm>
      </div>
    ),
  },
];

const OSDTable = ({ orderId, locationId, refreshKey, activeFilter = "All", onDataChange, onDelete, onEdit, warehouses = [] }) => {
  const [osdDetails, setOsdDetails] = useState([]);
  const [galleryImages, setGalleryImages] = useState(null);

  const fetchOsdData = async () => {
    if (!orderId) return;
    try {
      const res = await fetchOsdDetails(orderId);
      const data = res?.data || [];
      setOsdDetails(data);
      if (onDataChange) onDataChange(data);
    } catch (e) {
      console.error("Error fetching OSD details:", e);
    }
  };

  useEffect(() => {
    fetchOsdData();
  }, [orderId, refreshKey]);

  const getImages = (record) => {
    if (Array.isArray(record.images) && record.images.length) return record.images;
    if (record.picture_url) return [{ id: record.id, file_name: record.file_name, picture_url: record.picture_url }];
    return [];
  };

  const openGallery = (record) => {
    const images = getImages(record);
    if (!images.length) return;
    setGalleryImages(images.map(img => ({ src: img.picture_url })));
  };

  const filteredData = useMemo(() => {
    const status = FILTER_STATUS_MAP[activeFilter];
    if (!status) return osdDetails;
    return osdDetails.filter(item => item.status === status);
  }, [osdDetails, activeFilter]);

  const columns = useMemo(
    () => buildOsdColumns({ warehouses, getImages, openGallery, onEdit, onDelete }),
    [warehouses, getImages, openGallery, onEdit, onDelete]
  );

  return (
    <>
      <TableComponent
        columns={columns}
        dataSource={filteredData}
        rowKey="id"
        pagination={false}
        scroll={{ x: 1300 }}
      />
      {galleryImages && (
        <Gallery images={galleryImages} onClose={() => setGalleryImages(null)} />
      )}
    </>
  );
};

export default OSDTable;
